/**
 * SessionCalendarButton — Add a single session to calendar (Apple / Google / Outlook).
 *
 * Asks for a date + time, then opens the event via the existing
 * openCalendarDirect() helper (Google URL on Android, inline ICS elsewhere).
 */

import { useState } from "react";
import { CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import type { SessionRecord } from "@/hooks/useSessions";
import {
  openCalendarDirect,
  buildSessionDescription,
  getSessionEmoji,
  type CalendarEvent,
} from "@/lib/generateICS";

interface SessionCalendarButtonProps {
  session: SessionRecord;
  className?: string;
}

export function SessionCalendarButton({ session, className }: SessionCalendarButtonProps) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  });
  const [time, setTime] = useState("09:00");

  const handleConfirm = () => {
    if (!date || !time) {
      toast.error("Choisis une date et une heure");
      return;
    }

    const [h, m] = time.split(":").map(Number);
    const start = new Date(date);
    start.setHours(h || 9, m || 0, 0, 0);

    const durationMin = session.duration_minutes || 60;
    const end = new Date(start.getTime() + durationMin * 60_000);

    const emoji = getSessionEmoji(session.title, session.objective);

    const event: CalendarEvent = {
      uid: `perform-session-${session.id}@perform.app`,
      title: `${emoji} ${session.title}`,
      description: buildSessionDescription({
        title: session.title,
        objective: session.objective,
        exercises: session.exercises,
        sessionId: session.id,
        durationMinutes: durationMin,
      }),
      start,
      end,
      allDay: false,
      url: `${window.location.origin}/sessions?session=${session.id}`,
    };

    openCalendarDirect([event]);
    toast.success("Séance ajoutée au calendrier !");
    setOpen(false);
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className={`text-xs gap-1 ${className ?? ""}`}
        onClick={() => setOpen(true)}
      >
        <CalendarPlus className="h-3 w-3" />
        Ajouter au calendrier
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">📅 Ajouter au calendrier</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Choisis quand tu veux faire cette séance. L'événement sera ajouté à ton calendrier (Apple, Google, Outlook).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
              <p className="text-xs font-medium text-foreground truncate">{session.title}</p>
              <p className="text-[11px] text-muted-foreground">{session.duration_minutes || 60} min</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="text-xs h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Heure</Label>
                <Input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="text-xs h-9"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleConfirm}>
              <CalendarPlus className="h-3 w-3" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
